import {
	ActionKind,
	OrderItemStatus,
	useUpdateOrderItemStatusMutation,
	type GetOrderQuery,
} from '@codezo/graphql';
import { ActionBox, ActionContext } from '@codezo/order-detail';
import { TriangleAlert } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { ConfirmDialog } from '../../../components/ui/confirm-dialog';
import {
	UpdateOrderItemStatusModal,
	type OrderItemStatusAction,
	type OrderItemStatusFields,
} from '../../../components/orders/UpdateOrderItemStatusModal';
import { getMutationError } from '../../../lib/mutation-response';
import { isAdvancing, partitionItemActions, type ItemAction } from './itemActionRows';

type OrderItem = NonNullable<GetOrderQuery['getOrder']>['items'][number];

/**
 * The moves the studio can make on one step of one product.
 *
 * ── Why this exists at all ──
 *
 * The section chip said "Your action" and the rows offered nothing to do. The
 * chip was right — the server really was offering the studio moves — but the
 * spine never rendered them, so the page named an obligation it gave no way to
 * discharge.
 *
 * ── It is handed its actions, it does not choose them ──
 *
 * Which row a move belongs under is decided in `itemActionRows`, off the status
 * the move sets. This used to take the whole item and render everything it
 * found, which put "Start production" under **Sample & approval** — under the
 * one step it has nothing to do with.
 *
 * ── The contract is the backend's ──
 *
 * Which actions exist, what they are called, whether they are blocked and why,
 * and which fields they need are all fields. An action carrying
 * `requiredFields`/`optionalFields` opens the form modal; one carrying none is
 * a straight confirm — the same split v1 makes, against the same modal, so the
 * two surfaces can't drift on what "Mark shipped" asks for.
 */
/**
 * The buttons themselves, plus the dialogs they open.
 *
 * Split out from the box so the same moves can render in the spine's ActionBox
 * and bare on the product card, without a second copy of the mutation, the
 * confirm/form split, or the toasts.
 */
export function ActionButtons({
	item,
	actions,
	onChanged,
}: {
	item: OrderItem;
	actions: ItemAction[];
	onChanged: () => void;
}) {
	const [updateItemStatus, { loading }] = useUpdateOrderItemStatusMutation();
	const [confirmAction, setConfirmAction] = useState<{
		label: string;
		status: OrderItemStatus;
		/**
		 * What doing this NOW costs, in the server's words — "Starting production
		 * now means this product has not been paid for…".
		 *
		 * A warning, not a block. The action stays enabled and the mutation
		 * succeeds; this is a commercial judgement the studio is entitled to make,
		 * and the rules that used to refuse it were removed from the backend on
		 * purpose. Disabling the button here would put that rule back where it is
		 * harder to remove than it was there.
		 *
		 * It goes on the confirm because that is the last moment before it is true.
		 */
		warning?: string | null;
	} | null>(null);
	const [fieldAction, setFieldAction] = useState<OrderItemStatusAction | null>(null);

	const run = async (status: OrderItemStatus, extra?: OrderItemStatusFields) => {
		try {
			const { data } = await updateItemStatus({
				variables: { id: item.id, input: { status, ...extra } },
			});
			const error = getMutationError(data?.updateOrderItemStatus);
			if (error) {
				toast.error(error);
				return;
			}
			toast.success('Status updated');
			setConfirmAction(null);
			setFieldAction(null);
			onChanged();
		} catch {
			toast.error('Failed to update item');
		}
	};

	return (
		<>
			{actions.map((action) => (
				<Button
					key={action.label}
					size="xs"
					variant={
						action.status === OrderItemStatus.Cancelled ? 'destructive' : 'outline'
					}
					// Shown disabled with the server's reason rather than hidden: a
					// move with a precondition is information, and removing the button
					// only raises the question of where it went.
					disabled={!!action.blocked || loading}
					title={action.blocked ? (action.blockedReason ?? undefined) : undefined}
					onClick={() => {
						if (!action.status) return;
						const requiredFields = action.requiredFields ?? [];
						const optionalFields = action.optionalFields ?? [];
						if (requiredFields.length > 0 || optionalFields.length > 0) {
							setFieldAction({
								label: action.label,
								status: action.status,
								requiredFields,
								optionalFields,
							});
						} else {
							setConfirmAction({
								label: action.label,
								status: action.status,
								warning: action.warningReason,
							});
						}
					}}
				>
					{action.label}
				</Button>
			))}

			<ConfirmDialog
				open={!!confirmAction}
				onOpenChange={(open) => {
					if (!open) setConfirmAction(null);
				}}
				title={confirmAction?.label || ''}
				description={
					<>
						{`${confirmAction?.label} for "${item.productName || 'this item'}"?`}
						{/* Marked as a warning, not just written as one.
						    The sample modal gives this sentence a triangle and a box;
						    as plain text under the question it read as more of the
						    question. It is the one thing on this dialog that should
						    survive being skimmed — and the calmer wording makes the
						    mark carry more of the weight, not less. */}
						{confirmAction?.warning && (
							<span className="mt-3 flex gap-2 rounded-[var(--r-2)] border border-line-2 bg-surface-sunken px-3 py-2.5">
								<TriangleAlert
									size={14}
									className="mt-px shrink-0 text-status-warn"
									aria-hidden
								/>
								<span className="text-xs leading-relaxed text-text-2">
									{confirmAction.warning}
								</span>
							</span>
						)}
					</>
				}
				confirmLabel={confirmAction?.label || 'Confirm'}
				variant={
					confirmAction?.status === OrderItemStatus.Cancelled ? 'destructive' : 'default'
				}
				loading={loading}
				onConfirm={() => confirmAction && run(confirmAction.status)}
			/>

			<UpdateOrderItemStatusModal
				open={!!fieldAction}
				onOpenChange={(open) => {
					if (!open) setFieldAction(null);
				}}
				action={fieldAction}
				productName={item.productName || 'this item'}
				loading={loading}
				onSubmit={(fields) => fieldAction && run(fieldAction.status, fields)}
			/>
		</>
	);
}

export function ItemActions({
	item,
	actions,
	notices = [],
	context,
	onChanged,
}: {
	item: OrderItem;
	/** This row's moves, already selected by the caller. */
	actions: ItemAction[];
	/** The server's standing statements — messages, not buttons. */
	notices?: ItemAction[];
	/**
	 * Facts about the thing being acted on — the carrier and tracking beside
	 * "Mark delivered". Facts only, never a claim about readiness.
	 */
	context?: ReactNode;
	onChanged: () => void;
}) {
	if (actions.length === 0 && notices.length === 0) return null;

	// A standing notice outranks a blocked button's reason — v1's precedence, and
	// the right one: the notice names what everyone is waiting for, while the
	// reason only says why this one button is off.
	const blocked = actions.find((a) => a.blocked && a.blockedReason);
	// Tinted only when there is a move the studio can make NOW, and only one that
	// carries the item forward.
	const mine = actions.some((a) => !a.blocked && isAdvancing(a));

	return (
		<ActionBox mine={mine}>
			<ActionContext>
				{context && <span className="block">{context}</span>}

				{/* The server's own words for what everyone is waiting on. */}
				{notices.map((n) => (
					<span key={n.label} className="flex items-center gap-1.5 text-status-warn">
						<span className="h-1.5 w-1.5 shrink-0 rounded-full bg-status-warn" />
						<span>
							{n.label}
							{n.blockedReason ? ` — ${n.blockedReason}` : ''}
						</span>
					</span>
				))}

				{/* Suppressed when a notice is already saying it, so the box doesn't
				    state the same wait twice. */}
				{blocked && notices.length === 0 && (
					<span className="block text-text-4">{blocked.blockedReason}</span>
				)}
			</ActionContext>

			<ActionButtons item={item} actions={actions} onChanged={onChanged} />
		</ActionBox>
	);
}

/**
 * The moves that are not steps — cancelling an item.
 *
 * Rendered on the product card rather than in the flow spine. The design has no
 * cancel anywhere in the flow, and for a good reason: a spine is a sequence of
 * things that carry the order forward, and an escape hatch sitting inside one
 * reads as the next move. Left there it also broke two things around it — it is
 * never blocked, so it tinted every action box and made every product's chip say
 * "Your action" permanently.
 *
 * v1 keeps it on the product card too, so this is where a studio already looks.
 */
export function ItemEscapeActions({ item, onChanged }: { item: OrderItem; onChanged: () => void }) {
	const { buttons } = partitionItemActions(item);
	// The server says which these are now — `kind: ESCAPE` — rather than us
	// inferring it from a status that maps to no row.
	const escapes = buttons.filter((a) => a.kind === ActionKind.Escape);
	if (escapes.length === 0) return null;

	// Bare, not in an ActionBox: the card already provides the surface, and a box
	// here would be the empty-context strip the spine was rightly criticised for.
	return <ActionButtons item={item} actions={escapes} onChanged={onChanged} />;
}
